import { Component, EventEmitter, Output, inject } from '@angular/core';
import { ComponentAnchor } from 'src/models/anchor-and-version/component.model';
import { ComponentFilter } from 'src/filters/anchor-and-version-filter/component.filter';
import { ComponentService } from 'src/services/anchor-and-version-services/component.service';
import { FilterMetadata } from 'primeng/api';
import { FormControl } from '@angular/forms';
import { NotificationService } from 'src/services/notification.service';
import { TableLazyLoadEvent } from 'primeng/table';
import { TableProp } from 'src/utils/table-prop.util';
import { trimmedRequiredValidator } from 'src/validators/required.validator';

const filterConfigProps: TableProp[] = [
    { field: 'exclude', label: 'Exclude' },
    { field: 'displayName', label: 'Component Name' },
    { field: 'validVersion.fullName', label: 'Full Name' },
    { field: 'componentCodeAnchorKey', label: 'Component Code' },
    /* { field: 'type', label: 'Component Code Type' }, */
    { field: 'validVersion.generation', label: 'Generation' },
    { field: 'validVersion.isActive', label: 'Is Active' },
];

@Component({
    selector: 'app-create-filter-config-dialog',
    templateUrl: './create-filter-config-dialog.component.html',
    styleUrls: ['./create-filter-config-dialog.component.scss'],
    // eslint-disable-next-line @angular-eslint/prefer-standalone
    standalone: false,
})
export class CreateFilterConfigDialogComponent {
    @Output() public save = new EventEmitter<{ configName: string; excludedKeys: number[] }>();

    public componentAnchors: ComponentAnchor[] = [];
    public componentLimit = 15;
    public configName = new FormControl<string>('', trimmedRequiredValidator);
    public hasMore = false;
    public isLoading = false;
    public isVisible = false;
    public props = filterConfigProps;
    public selectedComponents: ComponentAnchor[] = [];
    public totalRecords = 0;

    private componentService = inject(ComponentService);
    private lastLazyEvent?: TableLazyLoadEvent;
    private notificationService = inject(NotificationService);

    public get displayedComponents(): ComponentAnchor[] {
        const selectedKeys = new Set(this.selectedComponents.map((c) => c.key));
        const unselected = this.componentAnchors.filter((c) => !selectedKeys.has(c.key));
        return [...this.selectedComponents, ...unselected];
    }

    public isLastComponent(component: ComponentAnchor): boolean {
        const displayed = this.displayedComponents;
        return displayed[displayed.length - 1]?.key === component.key;
    }

    public isSelectedComponent(component: ComponentAnchor): boolean {
        return this.selectedComponents.some((selected) => selected.key === component.key);
    }

    public async loadComponentAnchors(event: TableLazyLoadEvent): Promise<void> {
        if (!this.isVisible) return;

        this.lastLazyEvent = event;
        this.isLoading = true;
        try {
            const filter = this.parseEvent(event);
            const [result, total] = await Promise.all([
                this.componentService.getAnchorsWithValidVersion(filter),
                this.componentService.CountAnchorsWithValidVersion({
                    ...filter,
                    qLimit: undefined,
                }),
            ]);

            this.componentAnchors = result;
            this.totalRecords = total;
            this.hasMore = result.length === this.componentLimit;
        } catch (e) {
            this.notificationService.handleError('Failed to load component anchors', e);
        } finally {
            this.isLoading = false;
        }
    }

    public async loadMore(): Promise<void> {
        if (!this.lastLazyEvent) return;

        this.isLoading = true;
        try {
            const workspaceKey = await this.componentService['getWorkspaceKey']();
            if (!workspaceKey || workspaceKey === -1) return;

            const filter = this.parseEvent({
                ...this.lastLazyEvent,
                first: this.componentAnchors.length,
                rows: this.componentLimit,
            });

            const result = await this.componentService.getAnchorsWithValidVersion(filter);

            this.componentAnchors = [...this.componentAnchors, ...result];
            this.hasMore = result.length === this.componentLimit;
        } catch (e) {
            this.notificationService.handleError('Failed to load more components', e);
        } finally {
            this.isLoading = false;
        }
    }

    public onClose(): void {
        this.isVisible = false;
    }

    public async onOpen(): Promise<void> {
        this.configName.reset();
        this.selectedComponents = [];
        this.isVisible = true;
        this.loadComponentAnchors({ first: 0, rows: this.componentLimit });
    }

    public onSave(): void {
        this.save.emit({
            configName: this.configName.value || '',
            excludedKeys: this.selectedComponents.map((c) => c.key),
        });
        this.isVisible = false;
    }

    public toggleComponentSelection(component: ComponentAnchor): void {
        if (this.isSelectedComponent(component)) {
            this.selectedComponents = this.selectedComponents.filter(
                (selected) => selected.key !== component.key
            );
            return;
        }

        this.selectedComponents = [...this.selectedComponents, component];
    }

    private parseEvent(event: TableLazyLoadEvent): ComponentFilter {
        const filter: ComponentFilter = {
            qLimit: event.rows ?? this.componentLimit,
            qOffset: event.first ?? 0,
        };

        if (event.filters) {
            for (const key of Object.keys(event.filters)) {
                const meta = event.filters[key];
                const val = Array.isArray(meta) ? meta[0]?.value : (meta as FilterMetadata)?.value;
                if (val) {
                    const filterKey = key === 'displayName' ? 'qDisplayName' : key;
                    (filter as Record<string, unknown>)[filterKey] = val;
                }
            }
        }

        return filter;
    }
}
